import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useApolloClient, useMutation } from '@apollo/client';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import Lightbox from 'yet-another-react-lightbox';

import {
  ImageDeleteButton,
  imageDeleteRevealStyles,
} from '../../../components/ImageDeleteButton';
import {
  GET_CLOUDFLARE_DIRECT_LINK_QUERY,
  SET_SITE_TITLE_IMAGE_MUTATION,
} from '../../../gql';
import { handleUploadImage } from '../../../io';
import {
  clampCropPosition,
  CropPosition,
  DEFAULT_CROP_POSITION,
  getTitleCropRect,
  TITLE_ASPECT_RATIO,
} from './titleCrop';
import { FormattedMessage, useIntl } from 'react-intl';

const IMAGE_DELIVERY_URL = 'https://imagedelivery.net/3zdDZwDRXumMqC2LPbSSXw';
const RECROP_BUTTON_CLASS = 'site-title-rec-crop-button';
const acceptedImageTypes = {
  'image/jpeg': ['.jpeg', '.jpg'],
  'image/png': ['.png'],
};
const animationSettings = { fade: 150, swipe: 0 };
const titlePhotoStyles = {
  ...imageDeleteRevealStyles,
  position: 'relative',
  width: '100%',
  aspectRatio: `${TITLE_ASPECT_RATIO}`,
  overflow: 'hidden',
  borderRadius: 1,
  bgcolor: 'sunken',
  [`& .${RECROP_BUTTON_CLASS}`]: {
    opacity: 1,
    pointerEvents: 'auto',
  },
  '& .image-delete-button': {
    opacity: 1,
    pointerEvents: 'auto',
  },
};
const titlePhotoButtonStyles = {
  display: 'block',
  width: '100%',
  height: '100%',
};
const titleImageStyles = {
  display: 'block',
  width: '100%',
  height: '100%',
  objectFit: 'cover',
} as React.CSSProperties;
const recropButtonStyles = {
  position: 'absolute',
  top: 12,
  right: 56,
  minHeight: 34,
  px: 1.75,
  bgcolor: 'rgba(31, 34, 40, 0.88)',
  color: 'common.white',
  boxShadow: 2,
  backdropFilter: 'blur(8px)',
  transition:
    'opacity 0.15s ease, background-color 0.15s ease, color 0.15s ease',
  '&:hover': {
    bgcolor: 'rgba(49, 52, 60, 0.96)',
  },
};
const iconStyles = { fontSize: 18 };
const dropzoneStyles = {
  width: '100%',
  aspectRatio: `${TITLE_ASPECT_RATIO}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px dashed',
  borderColor: 'divider',
  borderRadius: 1,
  bgcolor: 'sunken',
  color: 'text.disabled',
  cursor: 'pointer',
  boxSizing: 'border-box',
  '&:focus-visible': {
    outline: '2px solid',
    outlineColor: 'primary.main',
    outlineOffset: 2,
  },
};
const disabledDropzoneStyles = {
  ...dropzoneStyles,
  cursor: 'default',
};
const dropzoneContentStyles = { px: 2, textAlign: 'center' };
const addPhotoIconStyles = { display: 'block', mx: 'auto', mb: 1 };
const loadingOverlayStyles = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  bgcolor: 'rgba(255, 255, 255, 0.7)',
};
const editorImageWrapperStyles = {
  position: 'relative',
  width: '100%',
  lineHeight: 0,
  overflow: 'hidden',
  bgcolor: 'common.black',
  touchAction: 'none',
  userSelect: 'none',
};
const editorImageStyles = {
  display: 'block',
  width: '100%',
  height: 'auto',
  pointerEvents: 'none',
} as React.CSSProperties;
const editorHintStyles = { color: 'text.secondary' };

interface Size {
  height: number;
  width: number;
}

interface CropEditorProps {
  imageId: string;
  initialPosition: CropPosition;
  saving: boolean;
  onCancel: () => void;
  onSave: (position: CropPosition) => void;
}

const CropEditor = React.memo(
  ({ imageId, initialPosition, saving, onCancel, onSave }: CropEditorProps) => {
    const intl = useIntl();
    const imageRef = useRef<HTMLImageElement>(null);
    const dragRef = useRef<{
      pointerX: number;
      pointerY: number;
      position: CropPosition;
    }>();
    const [size, setSize] = useState<Size>({ height: 0, width: 0 });
    const [position, setPosition] = useState(initialPosition);

    const measureImage = useCallback(() => {
      const currentImage = imageRef.current;

      if (currentImage) {
        setSize({
          height: currentImage.clientHeight,
          width: currentImage.clientWidth,
        });
      }
    }, []);

    useEffect(() => {
      const currentImage = imageRef.current;

      if (!currentImage || typeof ResizeObserver === 'undefined') {
        return undefined;
      }

      const observer = new ResizeObserver(measureImage);
      observer.observe(currentImage);

      return () => observer.disconnect();
    }, [measureImage]);

    const cropRect = useMemo(() => getTitleCropRect(size, position), [
      position,
      size,
    ]);
    const cropBoxStyles = useMemo(
      () => ({
        position: 'absolute',
        top: cropRect.top,
        left: cropRect.left,
        width: cropRect.width,
        height: cropRect.height,
        border: '2px dashed white',
        boxSizing: 'border-box',
        boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.58)',
        cursor: cropRect.maxX > 0 ? 'ew-resize' : 'ns-resize',
      }),
      [cropRect]
    );

    const handlePointerDown = useCallback(
      (event: React.PointerEvent<HTMLDivElement>) => {
        event.currentTarget.setPointerCapture(event.pointerId);
        dragRef.current = {
          pointerX: event.clientX,
          pointerY: event.clientY,
          position,
        };
      },
      [position]
    );
    const handlePointerMove = useCallback(
      (event: React.PointerEvent<HTMLDivElement>) => {
        const drag = dragRef.current;

        if (!drag) {
          return;
        }

        if (cropRect.maxX > 0) {
          const delta = ((event.clientX - drag.pointerX) / cropRect.maxX) * 100;
          setPosition({
            x: clampCropPosition(drag.position.x + delta),
            y: 50,
          });

          return;
        }

        if (cropRect.maxY > 0) {
          const delta = ((event.clientY - drag.pointerY) / cropRect.maxY) * 100;
          setPosition({
            x: 50,
            y: clampCropPosition(drag.position.y + delta),
          });
        }
      },
      [cropRect.maxX, cropRect.maxY]
    );
    const handlePointerEnd = useCallback(() => {
      dragRef.current = undefined;
    }, []);
    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        const step = 2;

        if (cropRect.maxX > 0 && event.key === 'ArrowLeft') {
          event.preventDefault();
          setPosition((current) => ({
            x: clampCropPosition(current.x - step),
            y: current.y,
          }));
        } else if (cropRect.maxX > 0 && event.key === 'ArrowRight') {
          event.preventDefault();
          setPosition((current) => ({
            x: clampCropPosition(current.x + step),
            y: current.y,
          }));
        } else if (cropRect.maxY > 0 && event.key === 'ArrowUp') {
          event.preventDefault();
          setPosition((current) => ({
            x: current.x,
            y: clampCropPosition(current.y - step),
          }));
        } else if (cropRect.maxY > 0 && event.key === 'ArrowDown') {
          event.preventDefault();
          setPosition((current) => ({
            x: current.x,
            y: clampCropPosition(current.y + step),
          }));
        }
      },
      [cropRect.maxX, cropRect.maxY]
    );
    const handleSave = useCallback(() => onSave(position), [onSave, position]);

    return (
      <Stack gap={1}>
        <Box sx={editorImageWrapperStyles}>
          <img
            ref={imageRef}
            src={`${IMAGE_DELIVERY_URL}/${imageId}/public`}
            alt={intl.formatMessage({
              id: 'site.titlePhoto.cropImageAlt',
              defaultMessage: 'Recrop site title photo',
            })}
            draggable={false}
            style={editorImageStyles}
            onLoad={measureImage}
          />
          <Box
            role="slider"
            aria-label={intl.formatMessage({
              id: 'site.titlePhoto.cropPosition',
              defaultMessage: 'Title photo crop position',
            })}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={cropRect.maxX > 0 ? position.x : position.y}
            tabIndex={0}
            sx={cropBoxStyles}
            onKeyDown={handleKeyDown}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerEnd}
            onPointerCancel={handlePointerEnd}
          />
        </Box>
        <Typography variant="caption" sx={editorHintStyles}>
          <FormattedMessage
            id="site.titlePhoto.cropHelp"
            defaultMessage="Drag the dashed crop area to choose the visible part of the title photo."
          />
        </Typography>
        <Stack direction="row" gap={1}>
          <Button variant="contained" disabled={saving} onClick={handleSave}>
            {saving ? (
              <FormattedMessage
                id="site.titlePhoto.saving"
                defaultMessage="Saving…"
              />
            ) : (
              <FormattedMessage
                id="site.titlePhoto.saveCrop"
                defaultMessage="Save crop"
              />
            )}
          </Button>
          <Button disabled={saving} onClick={onCancel}>
            <FormattedMessage id="action.cancel" defaultMessage="Cancel" />
          </Button>
        </Stack>
      </Stack>
    );
  }
);

interface SiteTitlePhotoProps {
  image?: string | null;
  imageCropPosition?: CropPosition | null;
  readOnly: boolean;
  siteId: string;
  siteName?: string;
}

const SiteTitlePhoto = React.memo(
  ({
    image,
    imageCropPosition,
    readOnly,
    siteId,
    siteName,
  }: SiteTitlePhotoProps) => {
    const intl = useIntl();
    const client = useApolloClient();
    const [setSiteTitleImage] = useMutation(SET_SITE_TITLE_IMAGE_MUTATION);
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editing, setEditing] = useState(false);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [error, setError] = useState<string>();
    const cropPosition = imageCropPosition || DEFAULT_CROP_POSITION;

    const saveTitleImage = useCallback(
      async (imageId: string | null, position: CropPosition | null) => {
        const { data } = await setSiteTitleImage({
          variables: {
            siteId,
            imageId,
            cropPosition: position,
          },
        });

        if (!data?.setSiteTitleImage?.site) {
          throw new Error('The title photo could not be updated.');
        }
      },
      [setSiteTitleImage, siteId]
    );

    const handleFilesAccepted = useCallback(
      async (files: File[]) => {
        const file = files[0];

        if (!file) {
          return;
        }

        setError(undefined);
        setUploading(true);

        try {
          const { data } = await client.query({
            query: GET_CLOUDFLARE_DIRECT_LINK_QUERY,
            fetchPolicy: 'no-cache',
            variables: { name: file.name },
          });
          const { uploadURL, id } = data.getCloudflareDirectLink;

          if (!uploadURL || !id) {
            throw new Error('Failed to retrieve a direct upload URL.');
          }

          const imageId = await handleUploadImage(file, uploadURL, id);

          if (!imageId) {
            throw new Error('The image upload did not return an image ID.');
          }

          await saveTitleImage(imageId, DEFAULT_CROP_POSITION);
        } catch (uploadError) {
          console.error(uploadError);
          setError(
            intl.formatMessage({
              id: 'site.titlePhoto.uploadError',
              defaultMessage:
                'Uploading the title photo failed. Please try again.',
            })
          );
        } finally {
          setUploading(false);
        }
      },
      [client, intl, saveTitleImage]
    );
    const handleFilesRejected = useCallback(() => {
      setError(
        intl.formatMessage({
          id: 'site.titlePhoto.chooseImage',
          defaultMessage: 'Choose one JPG or PNG image.',
        })
      );
    }, [intl]);
    const { getInputProps, getRootProps } = useDropzone({
      accept: acceptedImageTypes,
      disabled: uploading || readOnly,
      maxFiles: 1,
      multiple: false,
      onDropAccepted: handleFilesAccepted,
      onDropRejected: handleFilesRejected,
    });
    const dropzoneRootProps = getRootProps({ className: 'dropzone' });
    const dropzoneInputProps = getInputProps();

    const handleOpenLightbox = useCallback(() => setLightboxOpen(true), []);
    const handleCloseLightbox = useCallback(() => setLightboxOpen(false), []);
    const handleStartEditing = useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setEditing(true);
      },
      []
    );
    const handleActionMouseDown = useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => event.stopPropagation(),
      []
    );
    const handleCancelEditing = useCallback(() => setEditing(false), []);
    const handleSaveCrop = useCallback(
      async (position: CropPosition) => {
        if (!image) {
          return;
        }

        setError(undefined);
        setSaving(true);

        try {
          await saveTitleImage(image, position);
          setEditing(false);
        } catch (saveError) {
          console.error(saveError);
          setError(
            intl.formatMessage({
              id: 'site.titlePhoto.cropError',
              defaultMessage: 'Saving the title crop failed. Please try again.',
            })
          );
        } finally {
          setSaving(false);
        }
      },
      [image, intl, saveTitleImage]
    );
    const handleClear = useCallback(async () => {
      if (
        !window.confirm(
          intl.formatMessage({
            id: 'site.titlePhoto.confirmRemove',
            defaultMessage:
              'Remove this title photo? It will remain available in site images.',
          })
        )
      ) {
        return;
      }

      setError(undefined);
      setSaving(true);

      try {
        await saveTitleImage(null, null);
      } catch (clearError) {
        console.error(clearError);
        setError(
          intl.formatMessage({
            id: 'site.titlePhoto.removeError',
            defaultMessage:
              'Removing the title photo failed. Please try again.',
          })
        );
      } finally {
        setSaving(false);
      }
    }, [intl, saveTitleImage]);

    const previewImageStyles = useMemo(
      () => ({
        ...titleImageStyles,
        objectPosition: `${cropPosition.x}% ${cropPosition.y}%`,
      }),
      [cropPosition.x, cropPosition.y]
    );
    const slides = useMemo(() => {
      return image ? [{ src: `${IMAGE_DELIVERY_URL}/${image}/public` }] : [];
    }, [image]);
    const lightboxLabels = useMemo(
      () => ({
        Close: intl.formatMessage({
          id: 'action.close',
          defaultMessage: 'Close',
        }),
        Previous: intl.formatMessage({
          id: 'image.previous',
          defaultMessage: 'Previous image',
        }),
        Next: intl.formatMessage({
          id: 'image.next',
          defaultMessage: 'Next image',
        }),
      }),
      [intl]
    );

    if (editing && image) {
      return (
        <Stack gap={1}>
          <CropEditor
            imageId={image}
            initialPosition={cropPosition}
            saving={saving}
            onCancel={handleCancelEditing}
            onSave={handleSaveCrop}
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      );
    }

    if (!image) {
      return (
        <Stack gap={1}>
          <Box
            sx={readOnly ? disabledDropzoneStyles : dropzoneStyles}
            {...dropzoneRootProps}
          >
            <input {...dropzoneInputProps} />
            <Box sx={dropzoneContentStyles}>
              {uploading ? (
                <CircularProgress size={24} />
              ) : (
                <>
                  <AddPhotoAlternateIcon sx={addPhotoIconStyles} />
                  <Typography>
                    {readOnly ? (
                      <FormattedMessage
                        id="site.titlePhoto.none"
                        defaultMessage="No title photo"
                      />
                    ) : (
                      <FormattedMessage
                        id="site.titlePhoto.dropzone"
                        defaultMessage="Drop a title photo here, or click to choose one"
                      />
                    )}
                  </Typography>
                  {!readOnly && (
                    <Typography variant="caption">
                      <FormattedMessage
                        id="site.titlePhoto.fileTypes"
                        defaultMessage="JPG or PNG"
                      />
                    </Typography>
                  )}
                </>
              )}
            </Box>
          </Box>
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      );
    }

    return (
      <Stack gap={1}>
        <Box sx={titlePhotoStyles}>
          <ButtonBase
            aria-label={intl.formatMessage(
              {
                id: 'site.titlePhoto.open',
                defaultMessage: 'Open {siteName} title photo',
              },
              {
                siteName:
                  siteName ||
                  intl.formatMessage({
                    id: 'site.genericName',
                    defaultMessage: 'Site',
                  }),
              }
            )}
            onClick={handleOpenLightbox}
            sx={titlePhotoButtonStyles}
          >
            <img
              src={`${IMAGE_DELIVERY_URL}/${image}/w=1280`}
              alt={intl.formatMessage(
                {
                  id: 'site.titlePhoto.alt',
                  defaultMessage: '{siteName} title photo',
                },
                {
                  siteName:
                    siteName ||
                    intl.formatMessage({
                      id: 'site.genericName',
                      defaultMessage: 'Site',
                    }),
                }
              )}
              style={previewImageStyles}
            />
          </ButtonBase>
          {!readOnly && (
            <>
              <Button
                className={RECROP_BUTTON_CLASS}
                aria-label={intl.formatMessage({
                  id: 'site.titlePhoto.recrop',
                  defaultMessage: 'Recrop title photo',
                })}
                title={intl.formatMessage({
                  id: 'site.titlePhoto.recrop',
                  defaultMessage: 'Recrop title photo',
                })}
                size="small"
                startIcon={<CameraAltOutlinedIcon sx={iconStyles} />}
                disabled={saving}
                onClick={handleStartEditing}
                onMouseDown={handleActionMouseDown}
                sx={recropButtonStyles}
              >
                <FormattedMessage
                  id="site.titlePhoto.edit"
                  defaultMessage="Edit photo"
                />
              </Button>
              <ImageDeleteButton
                title={intl.formatMessage({
                  id: 'site.titlePhoto.remove',
                  defaultMessage: 'Remove title photo',
                })}
                disabled={saving}
                onDelete={handleClear}
                offset={12}
              />
            </>
          )}
          {saving && (
            <Box sx={loadingOverlayStyles}>
              <CircularProgress size={24} />
            </Box>
          )}
        </Box>
        {error && <Alert severity="error">{error}</Alert>}
        <Lightbox
          animation={animationSettings}
          slides={slides}
          labels={lightboxLabels}
          open={lightboxOpen}
          close={handleCloseLightbox}
        />
      </Stack>
    );
  }
);

export { SiteTitlePhoto };
